import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AppModal from "../components/AppModal";
import AdministradorPagosModal from "../components/AdministradorPagosModal";
import { formatDate, formatDateTime } from "../utils/dateUtils";

const AdministrarBienesInmuebles = () => {
  const navigate = useNavigate();
  const [inmuebles, setInmuebles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    TBNoMatricula: "",
    TBDireccion: "",
    TBDireccion_id: null,
    TCId: "11001", // Default city ID (Bogota)
    TBTipo: "RESIDENCIAL",
    TBObs: "",
    numero_contrato_energia: "",
    numero_contrato_agua: "",
    numero_contrato_gas: "",
    estrato: "3",
    owner_persona: "",
    pin_verificacion: "",
    fecha_hora_impresion: "",
    situacion_propiedad: ""
  });

  const [snrStatus, setSnrStatus] = useState(null);
  const [verificandoSNR, setVerificandoSNR] = useState(false);

  const [ciudades, setCiudades] = useState([]);
  const [arrendadores, setArrendadores] = useState([]);
  const [certificadoFile, setCertificadoFile] = useState(null);
  const [direccionesSugeridas, setDireccionesSugeridas] = useState([]);

  const fileInputRef = useRef(null);
  const [uploadingInmuebleId, setUploadingInmuebleId] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrls, setImageModalUrls] = useState([]);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const [pagosModalOpen, setPagosModalOpen] = useState(false);
  const [selectedContratoId, setSelectedContratoId] = useState(null);

  const token = localStorage.getItem("token");

  const getMediaUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return path.startsWith("/") ? `${process.env.REACT_APP_API_URL || ""}${path}` : `${process.env.REACT_APP_API_URL || ""}/${path}`;
  };

  const openImageModal = (urls, index) => {
    setImageModalUrls(urls);
    setImageModalIndex(index);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setImageModalUrls([]);
    setImageModalIndex(0);
  };

  const openPagosModal = (id) => {
    setSelectedContratoId(id);
    setPagosModalOpen(true);
  };

  const showPreviousImage = (e) => {
    e.stopPropagation();
    setImageModalIndex((prev) => (prev <= 0 ? imageModalUrls.length - 1 : prev - 1));
  };

  const showNextImage = (e) => {
    e.stopPropagation();
    setImageModalIndex((prev) => (prev >= imageModalUrls.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!token) {
      showModal("Atención", "No estás logueado", "error");
      navigate("/login");
      return;
    }
    fetchCiudades();
    fetchInmuebles();
    fetchArrendadores();
  }, [token, navigate]);

  const fetchArrendadores = () => {
    // Si hay un endpoint para obtener los arrendadores de la organizacion, lo usamos
    // Por simplicidad en este MVP, podríamos consultar personas, o un endpoint custom.
    // Usaremos /api/persona/ (si el backend lo filtra) o puedes dejarlo como texto.
    axios.get(`${process.env.REACT_APP_API_URL || ""}/api/persona/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(res => setArrendadores(res.data))
      .catch(err => console.warn("No se pudieron cargar arrendadores", err));
  };

  const fetchCiudades = () => {
    axios.get(`${process.env.REACT_APP_API_URL || ""}/api/ciudades/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(res => setCiudades(res.data))
      .catch(err => {
        console.warn("No se pudieron cargar las ciudades", err);
      });
  };

  const fetchInmuebles = () => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(res => setInmuebles(res.data))
      .catch(err => {
        showModal("Error", "Error al cargar inmuebles", "error");
      })
      .finally(() => setLoading(false));
  };

  const showModal = (title, message, type = "info") => {
    setModal({ isOpen: true, title, message, type });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prevForm => ({ ...prevForm, [name]: value.toUpperCase() }));
  };

  const handleMatriculaBlur = (e) => {
    const matricula = e.target.value;
    if (matricula) {
      axios.get(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/buscar_por_matricula/?matricula=${matricula}`, {
        headers: { Authorization: `Token ${token}` },
      })
        .then(res => setDireccionesSugeridas(res.data))
        .catch(err => console.error(err));
    } else {
      setDireccionesSugeridas([]);
    }
  };

  const handleCertificadoChange = (e) => {
    setCertificadoFile(e.target.files[0]);
  };

  const normalizeDireccion = async (direccion, ciudadId) => {
    const cleanDireccion = String(direccion || "").trim();
    if (!cleanDireccion) {
      return { direccion: "", direccionId: null };
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/api/direcciones/normalizar/`,
        { direccion: cleanDireccion, ciudad_id: parseInt(ciudadId, 10) || null },
        { headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data?.datos) {
        return {
          direccion: response.data.datos.direccion_normalizada,
          direccionId: response.data.datos.id,
        };
      }
    } catch (err) {
      console.warn("Error normalizando dirección; se usará el valor original.", err);
    }

    return { direccion: cleanDireccion, direccionId: null };
  };

  const handleVerificarPinSNR = async () => {
    if (!form.pin_verificacion) {
      return showModal("PIN Requerido", "Por favor ingresa el Número de PIN del Certificado de Tradición para verificar ante la Supernotariado.", "error");
    }

    setVerificandoSNR(true);
    setSnrStatus({ loading: true, verificado: false, mensaje: "Consultando portal de la Supernotariado (validate.snr) y comprobando Matrícula, Fecha y PIN...", estado: "CARGANDO" });

    try {
      const fd = new FormData();
      fd.append("pin", form.pin_verificacion.trim());
      if (form.TBNoMatricula) {
        fd.append("matricula", form.TBNoMatricula.trim());
      }
      if (form.fecha_hora_impresion) {
        fd.append("fecha_finalizacion_generacion", form.fecha_hora_impresion.trim());
      }
      if (certificadoFile) {
        fd.append("certificado_tradicion", certificadoFile);
      }

      const res = await axios.post(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/verificar-pin-snr/`, fd, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setSnrStatus({
        loading: false,
        verificado: res.data.valido,
        mensaje: res.data.mensaje,
        estado: res.data.estado,
        detalle: res.data.detalle,
        comparacion: res.data.comparacion,
        fecha_verificacion: res.data.fecha_verificacion,
        matricula_snr: res.data.matricula_snr,
        fecha_snr: res.data.fecha_snr
      });

      // Auto-completar matrícula o fecha si la SNR o el PDF las retornaron y estaban vacías
      if (res.data.fecha_snr && !form.fecha_hora_impresion) {
        setForm(prev => ({ ...prev, fecha_hora_impresion: res.data.fecha_snr }));
      }
      if (res.data.matricula_snr && !form.TBNoMatricula) {
        setForm(prev => ({ ...prev, TBNoMatricula: res.data.matricula_snr }));
      }

      if (res.data.valido) {
        showModal("Verificación SNR Exitosa", res.data.mensaje, "success");
      } else {
        showModal("Resultado de Validación SNR", res.data.mensaje, "error");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.mensaje || err.response?.data?.detail || "Error al conectar con la Superintendencia de Notariado y Registro.";
      setSnrStatus({
        loading: false,
        verificado: false,
        mensaje: errorMsg,
        estado: "ERROR"
      });
      showModal("Error SNR", errorMsg, "error");
    } finally {
      setVerificandoSNR(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!editingId && !certificadoFile) {
      return showModal(
        "Certificado de Tradición Obligatorio",
        "Para la creación de un Bien Inmueble es obligatorio aportar el Certificado de Tradición (archivo PDF).",
        "error"
      );
    }

    if (!form.pin_verificacion || form.pin_verificacion.trim().length === 0) {
      return showModal(
        "Número de PIN Requerido",
        "Debe suministrar el Número de PIN de verificación del Certificado de Tradición.",
        "error"
      );
    }

    if (!form.fecha_hora_impresion || form.fecha_hora_impresion.trim().length === 0) {
      return showModal(
        "Fecha Finalización Generación Requerida",
        "Debe indicar la Fecha Finalización Generación en formato 03/04/2025 03:20 PM que figura en el Certificado de Tradición.",
        "error"
      );
    }

    if (!form.situacion_propiedad || form.situacion_propiedad.trim().length === 0) {
      return showModal(
        "Situación de Propiedad Requerida",
        "Debe registrar la situación actual de propiedad que consta en el Certificado de Tradición (titulares, gravámenes, etc.).",
        "error"
      );
    }

    setSubmitting(true);

    const normalized = await normalizeDireccion(form.TBDireccion, form.TCId);
    const formData = new FormData();
    formData.append("TBNoMatricula", form.TBNoMatricula);
    formData.append("TBDireccion", normalized.direccion);
    if (normalized.direccionId) {
      formData.append("TBDireccion_id", normalized.direccionId);
    }
    formData.append("TCId", form.TCId);
    formData.append("TBTipo", form.TBTipo);
    formData.append("TBObs", form.TBObs);
    formData.append("numero_contrato_energia", form.numero_contrato_energia);
    formData.append("numero_contrato_agua", form.numero_contrato_agua);
    formData.append("numero_contrato_gas", form.numero_contrato_gas);
    formData.append("estrato", form.estrato);
    formData.append("pin_verificacion", form.pin_verificacion);
    formData.append("fecha_finalizacion_generacion", form.fecha_hora_impresion);
    formData.append("fecha_hora_impresion", form.fecha_hora_impresion);
    formData.append("situacion_propiedad", form.situacion_propiedad);

    if (form.owner_persona) {
      formData.append("owner_persona", form.owner_persona);
    }
    if (certificadoFile) {
      formData.append("certificado_tradicion", certificadoFile);
    }

    if (editingId) {
      axios.put(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/${editingId}/`, formData, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }).then(() => {
        showModal("Éxito", "Inmueble actualizado correctamente", "success");
        setEditingId(null);
        resetForm();
        fetchInmuebles();
      }).catch(err => {
        const data = err.response?.data;
        let errorMessage = "Error al actualizar el inmueble";
        if (data) {
          if (data.detail) {
            errorMessage = data.detail;
          } else if (typeof data === 'object') {
            const firstKey = Object.keys(data)[0];
            if (Array.isArray(data[firstKey])) {
              errorMessage = `${firstKey}: ${data[firstKey][0]}`;
            } else if (typeof data[firstKey] === 'string') {
              errorMessage = `${firstKey}: ${data[firstKey]}`;
            }
          }
        }
        showModal("Error", errorMessage, "error");
      }).finally(() => setSubmitting(false));
    } else {
      axios.post(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/`, formData, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }).then(() => {
        showModal("Éxito", "Inmueble creado correctamente", "success");
        resetForm();
        fetchInmuebles();
      }).catch(err => {
        const data = err.response?.data;
        let errorMessage = "Error al crear el inmueble";
        if (data) {
          if (data.detail) {
            errorMessage = data.detail;
          } else if (typeof data === 'object') {
            const firstKey = Object.keys(data)[0];
            if (Array.isArray(data[firstKey])) {
              errorMessage = `${firstKey}: ${data[firstKey][0]}`;
            } else if (typeof data[firstKey] === 'string') {
              errorMessage = `${firstKey}: ${data[firstKey]}`;
            }
          }
        }
        showModal("Error", errorMessage, "error");
      }).finally(() => setSubmitting(false));
    }
  };

  const handleEdit = (inmueble) => {
    setEditingId(inmueble.id);
    setForm({
      TBNoMatricula: inmueble.TBNoMatricula,
      TBDireccion: inmueble.TBDireccion,
      TBDireccion_id: inmueble.TBDireccion_id || null,
      TCId: inmueble.TCId,
      TBTipo: inmueble.TBTipo,
      TBObs: inmueble.TBObs || "",
      numero_contrato_energia: inmueble.numero_contrato_energia || "",
      numero_contrato_agua: inmueble.numero_contrato_agua || "",
      numero_contrato_gas: inmueble.numero_contrato_gas || "",
      estrato: inmueble.estrato || "3",
      owner_persona: inmueble.owner_persona || "",
      pin_verificacion: inmueble.pin_verificacion || "",
      fecha_hora_impresion: inmueble.fecha_hora_impresion || "",
      situacion_propiedad: inmueble.situacion_propiedad || ""
    });
    setSnrStatus(inmueble.snr_verificado ? {
      loading: false,
      verificado: true,
      mensaje: "Certificado verificado ante la SNR.",
      estado: inmueble.snr_estado,
      detalle: inmueble.snr_detalle
    } : null);
    setCertificadoFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este inmueble?")) {
      axios.delete(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/${id}/`, {
        headers: { Authorization: `Token ${token}` }
      }).then(() => {
        showModal("Éxito", "Inmueble eliminado correctamente", "success");
        fetchInmuebles();
      }).catch(err => {
        showModal("Error", err.response?.data?.detail || "Error al eliminar el inmueble", "error");
      });
    }
  };

  const resetForm = () => {
    setForm({
      TBNoMatricula: "",
      TBDireccion: "",
      TBDireccion_id: null,
      TCId: "11001",
      TBTipo: "RESIDENCIAL",
      TBObs: "",
      numero_contrato_energia: "",
      numero_contrato_agua: "",
      numero_contrato_gas: "",
      estrato: "3",
      owner_persona: "",
      pin_verificacion: "",
      fecha_hora_impresion: "",
      situacion_propiedad: ""
    });
    setSnrStatus(null);
    setCertificadoFile(null);
    setDireccionesSugeridas([]);
    setEditingId(null);
  };

  const handleUploadPhotoClick = (inmuebleId) => {
    setUploadingInmuebleId(inmuebleId);
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingInmuebleId) return;

    const formData = new FormData();
    formData.append('imagen', file);

    axios.post(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/${uploadingInmuebleId}/upload_photo/`, formData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }).then(() => {
      showModal("Éxito", "Foto subida correctamente", "success");
      fetchInmuebles();
    }).catch(err => {
      showModal("Error", "Error al subir la foto", "error");
    }).finally(() => {
      setUploadingInmuebleId(null);
      e.target.value = null; // reset input
    });
  };

  return (
    <AppShell
      eyebrow="Gestión"
      title="Administrar Bienes Inmuebles"
      subtitle="Crea y administra tus propiedades y sube fotografías para que otros puedan consultarlas."
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />

      <div className="app-content">
        <section className="app-surface">
          <div className="app-section-title">
            <h2>{editingId ? "Modificar Inmueble" : "Registrar Nuevo Inmueble"}</h2>
          </div>
          <form onSubmit={handleSave} className="app-form-grid" autoComplete="off">
            <div className="app-field">
              <label>No. Matrícula</label>
              <input
                type="text"
                name="TBNoMatricula"
                value={form.TBNoMatricula}
                onChange={handleFormChange}
                onBlur={handleMatriculaBlur}
                required
                data-no-uppercase
                className="app-input"
              />
            </div>
            <div className="app-field">
              <label>Dirección</label>
              <input
                type="text"
                name="TBDireccion"
                value={form.TBDireccion}
                onChange={handleFormChange}
                required
                list="direcciones-list"
                pattern="^[a-zA-Z]+\s[0-9a-zA-Z]+\s[0-9a-zA-Z]+\s\d+(?:\s.+)?$"
                title="Debe usar el formato: Vía Número Letra Número Letra Número Complemento. Ej: C 60 18 15 o K 21B 35 28 Ap 201"
                placeholder="Ej. K 21B 35 28 Ap 201"
                data-no-uppercase
                className="app-input"
              />
              <datalist id="direcciones-list">
                {direccionesSugeridas.map((dir, idx) => (
                  <option key={idx} value={dir} />
                ))}
              </datalist>
              <small style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                Formato: Vía Principal + Número + Número + Número [+ Complemento]. Ej: C 60 18 15
              </small>
            </div>
            <div className="app-field">
              <label>Ciudad</label>
              <select
                name="TCId"
                value={form.TCId}
                onChange={handleFormChange}
                required
                className="app-select"
              >
                <option value="">-- Seleccionar Ciudad --</option>
                {ciudades.map((ciudad) => (
                  <option key={ciudad.TCId} value={ciudad.TCId}>
                    {ciudad.TCNombre}{ciudad.TCDepartamento ? ` - ${ciudad.TCDepartamento}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="app-field">
              <label>Tipo de Inmueble</label>
              <select
                name="TBTipo"
                value={form.TBTipo}
                onChange={handleFormChange}
                required
                className="app-select"
              >
                <option value="RESIDENCIAL">RESIDENCIAL</option>
                <option value="COMERCIAL">COMERCIAL</option>
                <option value="INDUSTRIAL">INDUSTRIAL</option>
              </select>
            </div>

            <div className="app-field">
              <label>Estrato</label>
              <select
                name="estrato"
                value={form.estrato}
                onChange={handleFormChange}
                required
                className="app-select"
              >
                <option value="1">1 - Bajo-bajo</option>
                <option value="2">2 - Bajo</option>
                <option value="3">3 - Medio-bajo</option>
                <option value="4">4 - Medio</option>
                <option value="5">5 - Medio-alto</option>
                <option value="6">6 - Alto</option>
              </select>
            </div>
            
            <div className="app-field">
              <label>Propietario (Arrendador)</label>
              <select
                name="owner_persona"
                value={form.owner_persona}
                onChange={handleFormChange}
                className="app-select"
              >
                <option value="">-- Autocargar mi usuario o seleccionar --</option>
                {arrendadores.map((arr) => (
                  <option key={arr.TPNoDocumento} value={arr.TPNoDocumento}>
                    {arr.TPNombres} {arr.TPApellidos} ({arr.TPNoDocumento})
                  </option>
                ))}
              </select>
            </div>

            {/* ======================================================== */}
            {/* SECCIÓN CERTIFICADO DE TRADICIÓN Y VERIFICACIÓN SNR      */}
            {/* ======================================================== */}
            <div style={{ gridColumn: "1 / -1", background: '#f0f9ff', padding: '16px', borderRadius: '8px', border: '1px solid #bae6fd', marginTop: '8px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <strong style={{ fontSize: '15px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🏛️</span> Certificado de Tradición y Verificación Supernotariado (SNR)
                </strong>
                <a
                  href="https://certificados.supernotariado.gov.co/certificado/external/validation/validate.snr"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '12px', color: '#0284c7', textDecoration: 'underline' }}
                >
                  Portal SNR External Validation ↗
                </a>
              </div>
              <p style={{ fontSize: '12px', color: '#0f172a', margin: '0 0 14px 0' }}>
                Para registrar el Bien Inmueble debe aportar el Certificado de Tradición y los datos correspondientes. El sistema verifica el Nro. de PIN en la web oficial de la Superintendencia de Notariado y Registro.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                <div className="app-field">
                  <label>Certificado de Tradición (PDF) {!editingId && <span style={{ color: '#e11d48' }}>*</span>}</label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleCertificadoChange}
                    className="app-input"
                    required={!editingId}
                  />
                  {editingId && <small style={{ color: "#666", fontSize: "11px" }}>Dejar en blanco para conservar el archivo existente.</small>}
                </div>

                <div className="app-field">
                  <label>Nro. de PIN de Verificación <span style={{ color: '#e11d48' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      name="pin_verificacion"
                      value={form.pin_verificacion}
                      onChange={handleFormChange}
                      placeholder="Ej: 20240101-12345678"
                      className="app-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleVerificarPinSNR}
                      disabled={verificandoSNR || !form.pin_verificacion}
                      className="app-button app-button--secondary"
                      style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}
                      title="Verificar PIN en https://certificados.supernotariado.gov.co/certificado/external/validation/validate.snr"
                    >
                      {verificandoSNR ? "Verificando..." : "Validar PIN SNR"}
                    </button>
                  </div>
                  {snrStatus && (
                    <div style={{ marginTop: '8px', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', backgroundColor: snrStatus.verificado ? '#ecfdf5' : '#fef2f2', border: `1px solid ${snrStatus.verificado ? '#a7f3d0' : '#fecaca'}`, color: snrStatus.verificado ? '#065f46' : '#991b1b' }}>
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        {snrStatus.verificado ? "✓ Verificación Conforme: " : "⚠️ Alerta de Verificación: "}
                      </strong>
                      <div>{snrStatus.mensaje}</div>
                      {snrStatus.comparacion && (
                        <div style={{ marginTop: "6px", fontSize: "11px", display: "grid", gap: "2px" }}>
                          <div>{snrStatus.comparacion.matricula_coincide ? "✓" : "✗"} a. Matrícula Inmobiliaria comparada</div>
                          <div>{snrStatus.comparacion.fecha_generacion_coincide ? "✓" : "✗"} b. Fecha Finalización Generación (03/04/2025 03:20 PM) comparada</div>
                          <div>{snrStatus.comparacion.pin_adjuntado_coincide ? "✓" : "✗"} c. PIN verificado con Certificado de Tradición adjuntado</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="app-field">
                  <label>Fecha Finalización Generación (dd/mm/aaaa hh:mm PM) <span style={{ color: '#e11d48' }}>*</span></label>
                  <input
                    type="text"
                    name="fecha_hora_impresion"
                    value={form.fecha_hora_impresion}
                    onChange={handleFormChange}
                    placeholder="03/04/2025 03:20 PM"
                    className="app-input"
                    required
                  />
                  <small style={{ color: '#64748b', fontSize: '11px' }}>Formato: 03/04/2025 03:20 PM (como figura en el Certificado de Tradición de la SNR).</small>
                </div>

                <div className="app-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Situación Actual de Propiedad <span style={{ color: '#e11d48' }}>*</span></label>
                  <textarea
                    name="situacion_propiedad"
                    value={form.situacion_propiedad}
                    onChange={handleFormChange}
                    rows="2"
                    placeholder="Ej: Propietario actual según anotación registral, libre de gravámenes, sin embargos ni patrimonio inembargable."
                    className="app-textarea"
                    required
                  />
                  <small style={{ color: '#64748b', fontSize: '11px' }}>Indique la situación jurídica actual según consta en el Certificado.</small>
                </div>
              </div>
            </div>

            <div className="app-field">
              <label>No. Contrato Energía (Opcional)</label>
              <input
                type="text"
                name="numero_contrato_energia"
                value={form.numero_contrato_energia}
                onChange={handleFormChange}
                data-no-uppercase
                className="app-input"
              />
            </div>
            <div className="app-field">
              <label>No. Contrato Agua (Opcional)</label>
              <input
                type="text"
                name="numero_contrato_agua"
                value={form.numero_contrato_agua}
                onChange={handleFormChange}
                data-no-uppercase
                className="app-input"
              />
            </div>
            <div className="app-field">
              <label>No. Contrato Gas (Opcional)</label>
              <input
                type="text"
                name="numero_contrato_gas"
                value={form.numero_contrato_gas}
                onChange={handleFormChange}
                data-no-uppercase
                className="app-input"
              />
            </div>

            <div className="app-field" style={{ gridColumn: "1 / -1" }}>
              <label>Observaciones</label>
              <textarea
                name="TBObs"
                value={form.TBObs}
                onChange={handleFormChange}
                rows="3"
                data-no-uppercase
                className="app-textarea"
              />
            </div>
            <div className="app-actions" style={{ gridColumn: "1 / -1" }}>
              <button className="app-button app-button--primary" type="submit" disabled={submitting}>
                {editingId ? "Actualizar Inmueble" : "Guardar Inmueble"}
              </button>
              {editingId && (
                <button className="app-button app-button--secondary" type="button" onClick={resetForm}>
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="app-surface">
          <div className="app-section-title">
            <h2>Mis Bienes Inmuebles</h2>
          </div>
          {loading ? (
            <p>Cargando...</p>
          ) : inmuebles.length === 0 ? (
            <div className="app-empty-state">
              <p>No tienes inmuebles registrados.</p>
            </div>
          ) : (
            <div className="app-grid app-grid--2">
              {inmuebles.map(inmueble => (
                <div key={inmueble.id} className="app-surface" style={{ padding: '16px', border: '1px solid #eee' }}>
                  <h3>{inmueble.TBDireccion}</h3>
                  <p><strong>Matrícula:</strong> {inmueble.TBNoMatricula}</p>
                  <p><strong>Tipo:</strong> {inmueble.TBTipo}</p>
                  <p>
                    <strong>Estado:</strong>{" "}
                    {inmueble.estado_arrendado ? (
                      <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Arrendado (Vigente)</span>
                    ) : (
                      <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Disponible / Contrato Finalizado</span>
                    )}
                  </p>

                  {/* Detalle de Certificado de Tradición y SNR */}
                  <div style={{ marginTop: '10px', padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#0369a1' }}>🏛️ Certificado SNR</span>
                      {inmueble.snr_verificado ? (
                        <span style={{ backgroundColor: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                          ✓ Verificado SNR
                        </span>
                      ) : (
                        <span style={{ backgroundColor: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                          {inmueble.snr_estado || 'Pendiente'}
                        </span>
                      )}
                    </div>
                    {inmueble.pin_verificacion && <div><strong>PIN SNR:</strong> {inmueble.pin_verificacion}</div>}
                    {inmueble.fecha_hora_impresion && <div><strong>Fecha Finalización Generación:</strong> {formatDateTime(inmueble.fecha_hora_impresion)}</div>}
                    {inmueble.situacion_propiedad && (
                      <div style={{ marginTop: '3px', color: '#475569' }}>
                        <strong>Situación Propiedad:</strong> {inmueble.situacion_propiedad}
                      </div>
                    )}
                    {inmueble.certificado_tradicion && (
                      <div style={{ marginTop: '6px' }}>
                        <a href={getMediaUrl(inmueble.certificado_tradicion)} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '500' }}>
                          📄 Descargar Certificado de Tradición
                        </a>
                      </div>
                    )}
                  </div>
                  {inmueble.todos_los_contratos && inmueble.todos_los_contratos.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', borderBottom: '2px solid var(--app-primary)', paddingBottom: '4px', display: 'inline-block' }}>Historial de Contratos</h4>
                      {inmueble.todos_los_contratos.map(contrato => (
                        <div key={contrato.id} style={{ padding: '12px', backgroundColor: contrato.vigente ? '#f0f9ff' : '#f9f9f9', borderLeft: `4px solid ${contrato.vigente ? '#3498db' : '#bdc3c7'}`, borderRadius: '4px', marginTop: '8px', marginBottom: '12px', fontSize: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '15px' }}>Contrato ID: {contrato.id}</strong>
                            {contrato.vigente ? (
                              <span style={{ backgroundColor: '#e1f5fe', color: '#0288d1', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>Vigente</span>
                            ) : (
                              <span style={{ backgroundColor: '#eeeeee', color: '#757575', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>Finalizado</span>
                            )}
                          </div>
                          <strong>Inicio:</strong> {formatDate(contrato.fecha_inicio)}<br />
                          {contrato.fecha_entrega && <><strong style={{ color: '#e74c3c' }}>Entrega Inmueble:</strong> {formatDate(contrato.fecha_entrega)}<br /></>}
                          <strong>Valor Canon:</strong> ${contrato.valor_canon}

                          {contrato.estado_cuenta && (
                            <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', backgroundColor: contrato.estado_cuenta.esta_al_dia ? '#f0fdf4' : '#fef2f2', border: `1px solid ${contrato.estado_cuenta.esta_al_dia ? '#bbf7d0' : '#fecaca'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                <span style={{ fontWeight: '700', fontSize: '13px', color: contrato.estado_cuenta.esta_al_dia ? '#166534' : '#991b1b' }}>
                                  {contrato.estado_cuenta.esta_al_dia
                                    ? '✓ Al día (Saldo: $0)'
                                    : `⚠️ Saldo Pendiente: $${new Intl.NumberFormat('es-CO').format(contrato.estado_cuenta.saldo)}`}
                                </span>
                                {contrato.estado_cuenta.meses_transcurridos > 0 && (
                                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                                    {contrato.estado_cuenta.meses_transcurridos} mes(es) causados
                                  </span>
                                )}
                              </div>
                              {contrato.estado_cuenta.canon_actual !== contrato.estado_cuenta.canon_inicial && (
                                <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '3px' }}>
                                  Canon actual vigente con IPC: ${new Intl.NumberFormat('es-CO').format(contrato.estado_cuenta.canon_actual)}
                                </div>
                              )}
                            </div>
                          )}

                          <div style={{ marginTop: '8px' }}>
                            <button
                              type="button"
                              className="app-table-button"
                              style={{
                                backgroundColor: '#2563eb',
                                color: '#ffffff',
                                borderColor: '#2563eb',
                                fontWeight: '600',
                                padding: '6px 12px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                              onClick={() => openPagosModal(contrato.id)}
                            >
                              💳 Administrar Pagos de Canon
                            </button>
                          </div>
                          
                          {contrato.duracion != null && (
                            <div style={{ marginTop: '4px' }}>
                              <strong>Duración:</strong> {contrato.duracion} {(
                                {
                                  DD: 'Días',
                                  MM: 'Meses',
                                  AA: 'Años'
                                }[contrato.tipo_duracion] || contrato.tipo_duracion
                              )}
                            </div>
                          )}

                          {contrato.personas && contrato.personas.length > 0 && (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd' }}>
                              <strong style={{ display: 'block', marginBottom: '5px' }}>Ocupantes / Responsables:</strong>
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {contrato.personas.map((p, idx) => (
                                  <li key={idx} style={{ marginBottom: '4px' }}>
                                    <span style={{ fontWeight: '500', color: '#2c3e50' }}>{p.rol}</span>: {p.nombre} <small>({p.documento} - {p.contacto})</small>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Sección de Reportes */}
                          {contrato.reportes && contrato.reportes.length > 0 ? (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd' }}>
                              <strong style={{ display: 'block', marginBottom: '8px', color: '#8e44ad' }}>Reportes y Novedades:</strong>
                              <div style={{ display: 'grid', gap: '8px' }}>
                                {contrato.reportes.map((rep) => (
                                  <div key={rep.id} style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', padding: '8px', borderRadius: '4px', fontSize: '13px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                      <strong>{rep.tipo}</strong>
                                      <span style={{ color: '#7f8c8d' }}>{formatDate(rep.fecha)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                      <span>Estado: <strong style={{ color: rep.estado === 'ABIERTO' ? '#e67e22' : '#27ae60' }}>{rep.estado}</strong></span>
                                      {rep.saldo > 0 && <span>Saldo: <strong style={{ color: '#e74c3c' }}>${rep.saldo}</strong></span>}
                                    </div>
                                    {rep.observacion && (
                                      <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#555' }}>
                                        "{rep.observacion}"
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd', color: '#7f8c8d', fontSize: '13px', fontStyle: 'italic' }}>
                              No hay reportes registrados para este contrato.
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {inmueble.numero_contrato_energia && <p><strong>Energía:</strong> {inmueble.numero_contrato_energia}</p>}
                  {inmueble.numero_contrato_agua && <p><strong>Agua:</strong> {inmueble.numero_contrato_agua}</p>}
                  {inmueble.numero_contrato_gas && <p><strong>Gas:</strong> {inmueble.numero_contrato_gas}</p>}
                  {inmueble.certificado_tradicion && (
                    <p>
                      <strong>Certificado:</strong> <a href={getMediaUrl(inmueble.certificado_tradicion)} target="_blank" rel="noopener noreferrer">Ver PDF</a>
                    </p>
                  )}
                  <p><strong>Fotos:</strong> {inmueble.fotos?.length || 0}</p>

                  {inmueble.fotos && inmueble.fotos.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', margin: '10px 0' }}>
                      {inmueble.fotos.map((foto, index) => (
                        <img
                          key={foto.id}
                          src={getMediaUrl(foto.imagen)}
                          alt="Inmueble"
                          onClick={() => openImageModal(inmueble.fotos.map(f => getMediaUrl(f.imagen)), index)}
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                        />
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button className="app-button app-button--secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => handleUploadPhotoClick(inmueble.id)}>
                      Subir Foto
                    </button>
                    <button className="app-button app-button--secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => handleEdit(inmueble)}>
                      Modificar
                    </button>
                    <button className="app-button app-button--danger" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => handleDelete(inmueble.id)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      {imageModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '16px',
          }}
          onClick={closeImageModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={closeImageModal}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ×
            </button>
            <button
              onClick={showPreviousImage}
              style={{
                position: 'absolute',
                left: '12px',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '20px',
              }}
            >
              ‹
            </button>
            <img
              src={imageModalUrls[imageModalIndex]}
              alt="Vista ampliada"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
              }}
            />
            <button
              onClick={showNextImage}
              style={{
                position: 'absolute',
                right: '12px',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '20px',
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}
      <AppModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ ...modal, isOpen: false })}
      />
      <AdministradorPagosModal
        isOpen={pagosModalOpen}
        onClose={() => setPagosModalOpen(false)}
        contratoId={selectedContratoId}
        onUpdated={fetchInmuebles}
      />
    </AppShell>
  );
};

export default AdministrarBienesInmuebles;


